import type { Data, DataList } from "@eai-toolkit/client";
import { DataApi } from "@eai-toolkit/client";
import { filtersToQ } from "@store/utils/queryUtils";
import { constructOrderParameter } from "@store/utils/sortUtils";

import { hydrateDataQueryStateFromString } from "../queryState";
import { baseApi, handleQueryError } from "./baseApi";
import {
  axiosInstance,
  fieldSortComparator,
  getAllResources,
  getSingleResource,
} from "./utils";

const dataApi = new DataApi(undefined, undefined, axiosInstance);

export const dataObjectsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllDataObjects: builder.query<Data[], { limit?: number } | void>({
      queryFn: async (options) => {
        try {
          const { items } = await getAllResources<Data>(
            (params) => dataApi.v1ClusterDataGet({ ...params, human: true }),
            options === undefined ? undefined : options,
          );
          return {
            data: items.sort(fieldSortComparator("name")) as Data[],
          };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "DataObject" as const, id })),
              { type: "DataObject", id: "LIST" },
            ]
          : [{ type: "DataObject", id: "LIST" }],
    }),

    getDataObjectById: builder.query<Data, { id: string }>({
      queryFn: async ({ id: dataId }) => {
        try {
          const result = await getSingleResource(() =>
            dataApi.v1DataGetById({ dataId, human: true }),
          );
          if (result.error) {
            return {
              error: { status: "CUSTOM_ERROR", error: result.error },
            };
          }
          return { data: result.data };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (_result, _error, { id }) => [{ type: "DataObject", id }],
    }),

    updateDataObject: builder.mutation<Data, Partial<Data> & Pick<Data, "id">>({
      queryFn: async (patch) => {
        try {
          const { id, ...body } = patch;
          const response = await dataApi.v1DataPutById({
            dataId: id,
            dataPost: body as Data,
          });
          return { data: response.data as Data };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: "DataObject", id },
        { type: "DataObject", id: "LIST" },
      ],
    }),

    getDataObjects: builder.query<DataList, { queryKey: string }>({
      queryFn: async ({ queryKey }) => {
        try {
          const queryState = hydrateDataQueryStateFromString(queryKey);
          const {
            paging: { offset, limit },
            query: { filters, sort },
          } = queryState;

          const order = constructOrderParameter(sort.fieldName, sort.direction);

          const response = await dataApi.v1ClusterDataGet({
            offset,
            limit,
            q: filtersToQ(filters),
            order,
            human: true,
          });

          return { data: response.data };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...(result.items ?? []).map(({ id }) => ({
                type: "DataObject" as const,
                id,
              })),
              { type: "DataObject", id: "SEARCH_LIST" },
            ]
          : [{ type: "DataObject", id: "SEARCH_LIST" }],
    }),
  }),
});

export const {
  useGetAllDataObjectsQuery,
  useGetDataObjectByIdQuery,
  useUpdateDataObjectMutation,
  useGetDataObjectsQuery,
} = dataObjectsApi;
